import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { v2 } from "oc-threejs/short3";
import {
  assertSegmentIstLückenlosGefüllt,
  assertWarnungen,
  createElement,
  resetArrangement,
  warnungenMitPfostenIndex,
} from "./utils";
import type { Segment } from "../arrangement/Segment";
import type { ZElement } from "../arrangement/ZElement";
import type { Arrangement } from "../arrangement/Arrangement";
import {
  getSollZwischenabstand,
  getZwischenabstandAnSeite,
  LINKS,
} from "../arrangement/elementFunctions";

export default {
  name: "TestEcksituation",
};

function createCornerSituation() {
  resetArrangement();
  const agt = APP.arrangement;
  const breite = 5;
  agt.grundriss.setStandardForm("Rechteck", [breite, breite], [true, true, false, false]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  return agt;
}

export const testElementZuElement: Test = async () => {
  const agt = createCornerSituation();
  assertWarnungen(agt, []);
};

async function createDoppeltorRechts(s: Segment, agt: Arrangement): Promise<ZElement> {
  s.remove(s.getElemente()[2]);
  s.remove(s.getElemente()[0]);
  const tor = s.getElemente()[0];
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  tor.change({
    massAuswahlwert,
  });
  agt.update3d();
  return tor;
}

async function createDoppeltorLinks(s: Segment, agt: Arrangement): Promise<ZElement> {
  s.remove(s.getElemente()[2]);
  s.remove(s.getElemente()[0]);
  const tor = s.getElemente()[0];
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  tor.change({
    massAuswahlwert,
  });
  agt.update3d();
  const empty = s.getEmpties().find((p) => p);
  agt.selectedObjects.push(empty);
  await APP.arrangement.removeSelectedObjects();
  agt.update3d();
  return tor;
}

async function createEinzeltorRechts(s: Segment, agt: Arrangement): Promise<ZElement> {
  s.remove(s.getElemente()[2]);
  s.remove(s.getElemente()[0]);
  const tor = s.getElemente()[0];
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Einzeltor_Easy");
  tor.change({
    massAuswahlwert,
  });
  agt.update3d();
  return tor;
}

async function createEinzeltorLinks(s: Segment, agt: Arrangement): Promise<ZElement> {
  s.remove(s.getElemente()[2]);
  s.remove(s.getElemente()[0]);
  const tor = s.getElemente()[0];
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Einzeltor_Easy");
  tor.change({
    massAuswahlwert,
  });
  agt.update3d();
  const empty = s.getEmpties().find((p) => p);
  agt.selectedObjects.push(empty);
  await APP.arrangement.removeSelectedObjects();
  agt.update3d();
  return tor;
}

export const testDoppeltorZuElement: Test = async () => {
  const agt = createCornerSituation();
  const s: Segment = agt.segmente[0];
  const tor = await createDoppeltorRechts(s, agt);
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const außenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  await subTestContext("Anschlag innen", () => {
    tor.öffnungAuswahlwert = innenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Der Toranschlag kollidiert mit einem Zaunelement!",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("Anschlag außen", () => {
    tor.öffnungAuswahlwert = außenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, []);
  });
};

export const testElementZuDoppeltor: Test = async () => {
  const agt = createCornerSituation();
  const s: Segment = agt.segmente[1];
  const tor = await createDoppeltorLinks(s, agt);
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const außenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  await subTestContext("Anschlag innen", () => {
    tor.öffnungAuswahlwert = innenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Der Toranschlag kollidiert mit einem Zaunelement!",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("Anschlag außen", () => {
    tor.öffnungAuswahlwert = außenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, []);
  });
};

export const testZweiDoppeltore: Test = async () => {
  const agt = createCornerSituation();
  const s0: Segment = agt.segmente[0];
  const tor0 = await createDoppeltorRechts(s0, agt);
  const s1: Segment = agt.segmente[1];
  const tor1 = await createDoppeltorLinks(s1, agt);
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const außenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  await subTestContext("2 x Anschlag innen", () => {
    tor0.öffnungAuswahlwert = innenAuswahlwert;
    tor1.öffnungAuswahlwert = innenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Es dürfen keine zwei Tore an einem Eckpfosten stehen",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("1 x Anschlag innen, 1x Anschlag außen", () => {
    tor0.öffnungAuswahlwert = innenAuswahlwert;
    tor1.öffnungAuswahlwert = außenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Es dürfen keine zwei Tore an einem Eckpfosten stehen",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("1 x Anschlag außen, 1x Anschlag innen", () => {
    tor0.öffnungAuswahlwert = außenAuswahlwert;
    tor1.öffnungAuswahlwert = innenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Es dürfen keine zwei Tore an einem Eckpfosten stehen",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });

  await subTestContext("2 x Anschlag außen", () => {
    tor0.öffnungAuswahlwert = außenAuswahlwert;
    tor1.öffnungAuswahlwert = außenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, ["Es dürfen keine zwei Tore an einem Eckpfosten stehen"]);
  });
};

export const testElementZuEinzeltor: Test = async () => {
  const agt = createCornerSituation();
  const s: Segment = agt.segmente[1];
  const tor = await createEinzeltorLinks(s, agt);
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const außenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  const anschlagLinks = APP.richMetadata.findAuswahlwert("Anschlag_links");
  const anschlagRechts = APP.richMetadata.findAuswahlwert("Anschlag_rechts");
  await subTestContext("Anschlag innen Eckpfosten", () => {
    tor.öffnungAuswahlwert = innenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagRechts;
    agt.update3d();
    assertWarnungen(agt, [
      "Der Toranschlag kollidiert mit einem Zaunelement!",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("Anschlag innen nicht Eckpfosten", () => {
    tor.öffnungAuswahlwert = innenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagLinks;
    agt.update3d();
    assertWarnungen(agt, []);
  });
  await subTestContext("Anschlag außen nicht Eckpfosten", () => {
    tor.öffnungAuswahlwert = außenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagLinks;
    agt.update3d();
    assertWarnungen(agt, []);
  });
  await subTestContext("Anschlag außen Eckpfosten", () => {
    tor.öffnungAuswahlwert = außenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagRechts;
    agt.update3d();
    assertWarnungen(agt, []);
  });
};

export const testEinzeltorZuElement: Test = async () => {
  const agt = createCornerSituation();
  const s: Segment = agt.segmente[0];
  const tor = await createEinzeltorRechts(s, agt);
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const außenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  const anschlagLinks = APP.richMetadata.findAuswahlwert("Anschlag_links");
  const anschlagRechts = APP.richMetadata.findAuswahlwert("Anschlag_rechts");
  await subTestContext("Anschlag innen Eckpfosten", () => {
    tor.öffnungAuswahlwert = innenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagLinks;
    agt.update3d();
    assertWarnungen(agt, [
      "Der Toranschlag kollidiert mit einem Zaunelement!",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("Anschlag innen nicht Eckpfosten", () => {
    tor.öffnungAuswahlwert = innenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagRechts;
    agt.update3d();
    assertWarnungen(agt, []);
  });
  await subTestContext("Anschlag außen Eckpfosten", () => {
    tor.öffnungAuswahlwert = außenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagLinks;
    agt.update3d();
    assertWarnungen(agt, []);
  });
  await subTestContext("Anschlag außen nicht Eckpfosten", () => {
    tor.öffnungAuswahlwert = außenAuswahlwert;
    tor.anschlagAuswahlwert = anschlagRechts;
    agt.update3d();
    assertWarnungen(agt, []);
  });
};

export const testZweiEinzeltore: Test = async () => {
  const agt = createCornerSituation();
  const s0: Segment = agt.segmente[0];
  const tor0 = await createEinzeltorRechts(s0, agt);
  const s1: Segment = agt.segmente[1];
  const tor1 = await createEinzeltorLinks(s1, agt);
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const außenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  await subTestContext("2 x Anschlag innen", () => {
    tor0.öffnungAuswahlwert = innenAuswahlwert;
    tor1.öffnungAuswahlwert = innenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Es dürfen keine zwei Tore an einem Eckpfosten stehen",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("1 x Anschlag innen, 1x Anschlag außen", () => {
    tor0.öffnungAuswahlwert = innenAuswahlwert;
    tor1.öffnungAuswahlwert = außenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, [
      "Es dürfen keine zwei Tore an einem Eckpfosten stehen",
      "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
      "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
    ]);
  });
  await subTestContext("1 x Anschlag außen, 1x Anschlag innen", () => {
    tor0.öffnungAuswahlwert = außenAuswahlwert;
    tor1.öffnungAuswahlwert = innenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, ["Es dürfen keine zwei Tore an einem Eckpfosten stehen"]);
  });

  await subTestContext("2 x Anschlag außen", () => {
    tor0.öffnungAuswahlwert = außenAuswahlwert;
    tor1.öffnungAuswahlwert = außenAuswahlwert;
    agt.update3d();
    assertWarnungen(agt, ["Es dürfen keine zwei Tore an einem Eckpfosten stehen"]);
  });
};

export const testGapKorrektAnTorpfostenUMProfil: Test = async () => {
  const agt = createCornerSituation();
  const s: Segment = agt.segmente[0];
  const tor1 = await createEinzeltorRechts(s, agt);
  const tor2 = agt.segmente[1].getElemente()[2].change({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Einzeltor_Easy"),
  });
  agt.update3d();
  const innenAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  const anschlagRechts = APP.richMetadata.findAuswahlwert("Anschlag_rechts");
  tor1.öffnungAuswahlwert = innenAuswahlwert;
  tor1.anschlagAuswahlwert = anschlagRechts;
  agt.update3d();
  assertWarnungen(agt, []);
};

export const testGapKorrektAnTorpfostenUMProfilFillAfterTor: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  const breite = 1.9;
  agt.grundriss.setStandardForm("Rechteck", [breite, breite]);
  agt.update3d();
  const tor = createElement(agt.segmente[0], {
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Einzeltor_Easy"),
    size: v2(1.2, 1.8),
  });
  tor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_innen");
  tor.anschlagAuswahlwert = APP.richMetadata.findAuswahlwert("Anschlag_rechts");
  agt.segmente[0].setX(tor, 0, 0);
  agt.update3d();
  const empty = agt.segmente[1].empties[0];
  const defaultElement = agt.createDefaultElement();
  const ist_empty = getZwischenabstandAnSeite(empty, LINKS);
  const soll = getSollZwischenabstand(empty, defaultElement.meta, LINKS);
  empty.fillWithElements(defaultElement, {
    allowMassElement: true,
    allowToGrowToMaximum: true,
  });
  agt.update3d();
  const element = agt.segmente[1].getElemente()[0];
  const ist_element = getZwischenabstandAnSeite(element, LINKS);
  assert.equals(0, ist_empty, "ist_empty");
  assert.equals(0.02, soll, "soll");
  assert.equals(0.05, ist_element, "ist_element");
  assertWarnungen(agt, []);
};

export const testKollisionTStueck: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  const json = {
    inspiration: "Inspiration SYSTEM WPC CLASSIC Sand",
    zaunart: "Sichtschutz",
    serie: "SYSTEM",
    grundriss: {
      ecken: [
        {
          id: 4,
          punkt: {
            x: 3.9668800134636313,
            y: 2.580005048861962,
          },
        },
        {
          id: 1431,
          punkt: {
            x: 3.970666666666666,
            y: 4,
          },
        },
        {
          id: 4493,
          punkt: {
            x: 3.973600169328194,
            y: 5.100063498073082,
          },
        },
        {
          id: 4767,
          punkt: {
            x: 5.514650030050149,
            y: 4,
          },
        },
      ],
      kanten: [
        {
          ecken: [4, 1431],
        },
        {
          ecken: [1431, 4493],
        },
        {
          ecken: [1431, 4767],
        },
      ],
    },
    segmente: [
      {
        breite: 1.42,
        partsAndPfosten: [
          {
            type: "Element",
            breite: 1.315,
            höhe: 1.8,
            snap: 1,
            x0: 0.0525,
            füllungAuswahlwert: "SYSTEM WPC CLASSIC Sand",
            form: "Einzeltor",
            massAuswahlwert: "Einzeltor_Easy",
            profilPosition: 10,
            elementNr: 0,
            detailSerie: "SYSTEM WPC CLASSIC",
            key: "2499 SYSTEM WPC Classic Mass Einzeltor zweiseitig_15",
            lichteWeite: 1.34,
          },
        ],
        verteilungsstrategieAuswahlwert: "Individuell",
      },
      {
        breite: 1.1001,
        partsAndPfosten: [],
        verteilungsstrategieAuswahlwert: "Gleichmässig",
      },
      {
        breite: 1.544,
        partsAndPfosten: [],
        verteilungsstrategieAuswahlwert: "Gleichmässig",
      },
    ],
    pfostenartAuswahlwert: "Steckpfosten",
    pfostenfarbeAuswahlwert: "Anthrazit",
    bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
    elementhalterAuswahlwert: "2096 Elementhalter",
    verteilungsstrategieAuswahlwert: "LeerraumLinks",
  };
  agt.fromJSON(json);
  agt.update3d();
  assert.deepEquals(
    [
      ["Der Toranschlag kollidiert mit einem Zaunelement!", 1],
      ["Torflügel kollidiert im Öffnungsbereich mit anderen Elementen", -1],
      ["Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels", -1, -1],
    ],
    warnungenMitPfostenIndex()
  );
};

export const testSpeziellerWinkelElemente: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  const json = {
    inspiration: "Inspiration SYSTEM WPC PLATINUM XL Teak-Braun",
    zaunart: "Sichtschutz",
    serie: "SYSTEM",
    grundriss: {
      ecken: [
        {
          id: 30751,
          punkt: {
            x: 9.600000000000001,
            y: 6.1000000000000005,
          },
        },
        {
          id: 65716,
          punkt: {
            x: 11.59812461025423,
            y: 7.19661557189632,
          },
        },
        {
          id: 66019,
          punkt: {
            x: 12.803004571894888,
            y: 7.8015022859474445,
          },
        },
        {
          id: 70716,
          punkt: {
            x: 10.992268128137061,
            y: 6.103254733436936,
          },
        },
      ],
      kanten: [
        {
          ecken: [70716, 30751],
        },
        {
          ecken: [65716, 66019],
        },
        {
          ecken: [70716, 65716],
        },
      ],
    },
    segmente: [
      {
        breite: 1.3923,
        partsAndPfosten: [
          {
            type: "Element",
            breite: 1.3623,
            höhe: 1.845,
            snap: -1,
            isBreiteKonfigurierbar: true,
            x0: 0.015,
            füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Teak-Braun",
            füllungAuswahlwertIndividualisiert: false,
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            abschlussleisteAuswahlwert: "Abschlussleisten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilKategorieAuswahlwert: "Dekorprofile30cm_ZaunSets",
            profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
            profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
            form: "Rechteck",
            massAuswahlwert: "180x180_Easy",
            profilPosition: 5,
            showProfil: null,
            anschlussAufRechts: false,
            elementNr: 0,
            isStandardMass: false,
            detailSerie: "SYSTEM WPC PLATINUM XL",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            elementID: 0,
          },
        ],
        verteilungsstrategieAuswahlwert: "Gleichmässig",
      },
      {
        breite: 1.3482,
        partsAndPfosten: [
          {
            type: "Element",
            breite: 1.3182,
            höhe: 1.845,
            snap: -1,
            isBreiteKonfigurierbar: true,
            x0: 0.015,
            füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Teak-Braun",
            füllungAuswahlwertIndividualisiert: false,
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            abschlussleisteAuswahlwert: "Abschlussleisten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilKategorieAuswahlwert: "Dekorprofile30cm_ZaunSets",
            profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
            profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
            form: "Rechteck",
            massAuswahlwert: "180x180_Easy",
            profilPosition: 5,
            showProfil: null,
            anschlussAufRechts: false,
            elementNr: 1,
            isStandardMass: false,
            detailSerie: "SYSTEM WPC PLATINUM XL",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            elementID: 0,
          },
        ],
        verteilungsstrategieAuswahlwert: "Gleichmässig",
      },
      {
        breite: 1.25,
        partsAndPfosten: [
          {
            type: "Element",
            breite: 1.22,
            höhe: 1.845,
            snap: -1,
            isBreiteKonfigurierbar: true,
            x0: 0.015,
            füllungAuswahlwert: "SYSTEM WPC PLATINUM XL Teak-Braun",
            füllungAuswahlwertIndividualisiert: false,
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            abschlussleisteAuswahlwert: "Abschlussleisten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilKategorieAuswahlwert: "Dekorprofile30cm_ZaunSets",
            profilAuswahlwert: "Dekor Gamma Edelstahl/hoch",
            profilleisteAuswahlwert: "Dekor_Adapter_anthrazit",
            form: "Rechteck",
            massAuswahlwert: "180x180_Easy",
            profilPosition: 4,
            showProfil: null,
            anschlussAufRechts: false,
            elementNr: 2,
            isStandardMass: false,
            detailSerie: "SYSTEM WPC PLATINUM XL",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            elementID: 0,
          },
        ],
        verteilungsstrategieAuswahlwert: "Gleichmässig",
      },
    ],
    ecken: [
      {
        position: {
          x: 10.992268128137061,
          y: 0,
          z: 6.103254733436936,
        },
        pfosten: {
          type: "Pfosten",
          position: {
            x: 10.992268128137061,
            y: 0,
            z: 6.103254733436936,
          },
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          ankerInnen: false,
          ausgleichGefälle: false,
        },
      },
      {
        position: {
          x: 9.600000000000001,
          y: 0,
          z: 6.1000000000000005,
        },
        pfosten: {
          type: "Pfosten",
          position: {
            x: 9.600000000000001,
            y: 0,
            z: 6.1000000000000005,
          },
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Steckpfosten",
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          ankerInnen: false,
          ausgleichGefälle: false,
        },
      },
      {
        position: {
          x: 11.59812461025423,
          y: 0,
          z: 7.19661557189632,
        },
        pfosten: {
          type: "Pfosten",
          position: {
            x: 11.59812461025423,
            y: 0,
            z: 7.19661557189632,
          },
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "Elementhalter Weiß",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metalltorpfosten",
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          ankerInnen: false,
          ausgleichGefälle: false,
        },
      },
      {
        position: {
          x: 12.803004571894888,
          y: 0,
          z: 7.8015022859474445,
        },
        pfosten: {
          type: "Pfosten",
          position: {
            x: 12.803004571894888,
            y: 0,
            z: 7.8015022859474445,
          },
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Steckpfosten",
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          ankerInnen: false,
          ausgleichGefälle: false,
        },
      },
    ],
    pfostenartAuswahlwert: "Steckpfosten",
    pfostenfarbeAuswahlwert: "Anthrazit",
    bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
    elementhalterAuswahlwert: "Elementhalter Weiß",
    verteilungsstrategieAuswahlwert: "Gleichmässig",
  };
  agt.fromJSON(json);
  agt.update3d();
  assert.deepEquals([], warnungenMitPfostenIndex());
  for (const s of agt.segmente) {
    assertSegmentIstLückenlosGefüllt(s);
  }
  // Nun wechselt man das mittlere Element zu einem Tor
  const [s1, s2] = agt
    .getPfosten()
    .filter((p) => p.elemente.length == 2)
    .map((p) => new Set(p.elemente));
  const mittleresElement = s1.intersection(s2).first();
  mittleresElement.anschlagAuswahlwert = APP.richMetadata.findAuswahlwert("Anschlag_rechts");
  mittleresElement.torÖffnungswinkel = 0;
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Einzeltor_Easy");
  mittleresElement.change({
    massAuswahlwert,
  });
  agt.update3d();
  agt.update3d();
  assert.deepEquals([], warnungenMitPfostenIndex());
  for (const s of agt.segmente) {
    assertSegmentIstLückenlosGefüllt(s, true, 0.002);
  }
};
